import type { MeasurementAnswerInput } from '@/src/dal/measurement-dal'
import type {
  MeasurementDefinitionDetail,
  SessionStatus,
} from '@/src/dal/session-dal'

export type VoiceAssessmentMeasurementPhase =
  'instructions' | 'record' | 'questions'

export type VoiceAssessmentState = {
  /**
   * Always `true` once the session-backed flow has hydrated. Kept on
   * the state for backwards compatibility with helpers like
   * `getCurrentScreen` that distinguish the (unused) pre-start screen.
   */
  started: boolean
  /**
   * Cursor into `measurementDefinitions`. Equals `defs.length` when all
   * measurements have been answered and the personal reflection screen
   * should be shown.
   */
  measurementIndex: number
  measurementPhase: VoiceAssessmentMeasurementPhase
  personalReflection: string
  /** Parallel array, length = measurementCount. */
  confirmedRecordingUris: (string | null)[]
  /**
   * Parallel array, length = measurementCount. In-memory Blob captured
   * locally for the current browser session. Not persisted across
   * reloads; native uploads can fall back to `confirmedRecordingUris`.
   */
  confirmedRecordingBlobs: (Blob | null)[]
  /**
   * Parallel array, length = measurementCount. Backend measurement id
   * once `createMeasurement` has succeeded for that index. Used to
   * avoid duplicating create/upload calls during the same session.
   */
  persistedMeasurementIds: (number | null)[]
  /** Parallel array, length = measurementCount. */
  measurementQuestionAnswers: MeasurementAnswerInput[][]
}

export type VoiceAssessmentHydratePatch = Partial<VoiceAssessmentState>

export type VoiceAssessmentAction =
  | { type: 'set_measurement_index'; index: number }
  | { type: 'set_measurement_phase'; phase: VoiceAssessmentMeasurementPhase }
  | { type: 'set_personal_reflection'; value: string }
  | {
      type: 'set_measurement_confirmed_recording'
      index: number
      uri: string | null
      blob: Blob | null
    }
  | {
      type: 'set_measurement_persisted_id'
      index: number
      value: number | null
    }
  | {
      type: 'set_measurement_single_choice_answer'
      index: number
      questionId: number
      choiceIndex: number
    }
  | {
      type: 'toggle_measurement_multiple_choice_answer'
      index: number
      questionId: number
      choiceIndex: number
    }
  | { type: 'resize_measurements'; count: number }
  | { type: 'hydrate'; payload: VoiceAssessmentHydratePatch }

export interface VoiceAssessmentFlowController {
  measurementCount: number
  measurementDefinitions: MeasurementDefinitionDetail[]
  measurementLabels: (string | null)[]
  state: VoiceAssessmentState
  dispatch: (action: VoiceAssessmentAction) => void
  requestLeaveStandaloneAssessment: () => void
  isCompletingAssessment: boolean
  handleCompleteAssessment: () => void
  trackMeasurementRecordingCompleted: (index: number) => void
  trackMeasurementQuestionsCompleted: (index: number) => void
  /**
   * Creates the backend measurement (with mapped answers) and uploads
   * the in-memory recording for the given measurement index. Idempotent
   * for indices that have already been persisted in this session.
   */
  persistMeasurement: (index: number) => Promise<void>
  /** Index currently being persisted, or null when idle. */
  persistingMeasurementIndex: number | null
}

export function createInitialMeasurementUris(
  measurementCount: number,
): (string | null)[] {
  return Array.from({ length: measurementCount }, () => null)
}

export function createInitialMeasurementBlobs(
  measurementCount: number,
): (Blob | null)[] {
  return Array.from({ length: measurementCount }, () => null)
}

export function createInitialPersistedMeasurementIds(
  measurementCount: number,
): (number | null)[] {
  return Array.from({ length: measurementCount }, () => null)
}

export function createInitialMeasurementQuestionAnswers(
  measurementCount: number,
): MeasurementAnswerInput[][] {
  return Array.from({ length: measurementCount }, () => [])
}

export function measurementRequiresRecording(
  measurementDefinition:
    Pick<MeasurementDefinitionDetail, 'audioTrack'> | null | undefined,
): boolean {
  return measurementDefinition?.audioTrack?.trim().length != null
    ? measurementDefinition.audioTrack.trim().length > 0
    : false
}

export function countMeasurementsRequiringRecording(
  measurementDefinitions: readonly MeasurementDefinitionLike[],
): number {
  return measurementDefinitions.filter(measurementRequiresRecording).length
}

function measurementHasRecordingInstructions(
  measurementDefinition: MeasurementDefinitionLike | null | undefined,
): boolean {
  return (
    measurementRequiresRecording(measurementDefinition) &&
    (measurementDefinition?.recordingInstructions?.trim().length ?? 0) > 0
  )
}

export type CurrentScreen =
  | { kind: 'intro' }
  | { kind: 'recordingInstructions'; measurementIndex: number }
  | { kind: 'record'; measurementIndex: number }
  | { kind: 'questions'; measurementIndex: number }
  | { kind: 'reflection' }

export type MeasurementAdvanceTarget =
  | { kind: 'questions' }
  | { kind: 'measurement'; measurementIndex: number }
  | { kind: 'reflection'; measurementIndex: number }

type CurrentScreenStateInput = Pick<
  VoiceAssessmentState,
  'started' | 'measurementIndex' | 'measurementPhase'
>

type MeasurementDefinitionLike = Pick<
  MeasurementDefinitionDetail,
  'audioTrack'
> &
  Partial<
    Pick<MeasurementDefinitionDetail, 'questions' | 'recordingInstructions'>
  >
type ResumeMeasurementDefinitionLike = Pick<
  MeasurementDefinitionDetail,
  'audioTrack' | 'id'
>
type SessionResumeMeasurementLike = {
  id: number
  definition: { id: number }
  hasRecording: boolean
}

export interface BackendResumeState {
  measurementIndex: number
  persistedMeasurementIds: (number | null)[]
}

export type BackendResumeTarget =
  | { kind: 'measurement'; measurementIndex: number }
  | { kind: 'reflection' }
  | { kind: 'finish' }

function isBackendResumeMeasurementComplete(
  definition: ResumeMeasurementDefinitionLike,
  measurement: SessionResumeMeasurementLike,
): boolean {
  if (!measurementRequiresRecording(definition)) {
    return true
  }
  return measurement.hasRecording
}

/**
 * Single source of truth for what to render. Derives the current
 * screen from the cursor + explicit per-measurement phase. Confirming
 * a recording keeps the user on the recording success state until they
 * press Next, where the view decides whether to show questions or move on.
 */
export function getCurrentScreen(
  state: CurrentScreenStateInput,
  measurementDefinitions: readonly MeasurementDefinitionLike[],
): CurrentScreen {
  if (!state.started) {
    return { kind: 'intro' }
  }
  if (state.measurementIndex >= measurementDefinitions.length) {
    return { kind: 'reflection' }
  }
  const safeIndex = Math.max(state.measurementIndex, 0)
  const measurementDefinition = measurementDefinitions[safeIndex]
  if (measurementDefinition == null) {
    return { kind: 'questions', measurementIndex: safeIndex }
  }
  if (!measurementRequiresRecording(measurementDefinition)) {
    return { kind: 'questions', measurementIndex: safeIndex }
  }
  if (state.measurementPhase === 'questions') {
    return { kind: 'questions', measurementIndex: safeIndex }
  }
  if (
    state.measurementPhase === 'instructions' &&
    measurementHasRecordingInstructions(measurementDefinition)
  ) {
    return { kind: 'recordingInstructions', measurementIndex: safeIndex }
  }
  return { kind: 'record', measurementIndex: safeIndex }
}

export function getMeasurementAdvanceTarget({
  from,
  measurementIndex,
  measurementDefinitions,
}: {
  from: Extract<CurrentScreen['kind'], 'record' | 'questions'>
  measurementIndex: number
  measurementDefinitions: readonly MeasurementDefinitionLike[]
}): MeasurementAdvanceTarget {
  const measurementDefinition = measurementDefinitions[measurementIndex]
  const shouldShowQuestions =
    from === 'record' && (measurementDefinition?.questions?.length ?? 0) > 0

  if (shouldShowQuestions) {
    return { kind: 'questions' }
  }

  const nextMeasurementIndex = measurementIndex + 1
  if (nextMeasurementIndex >= measurementDefinitions.length) {
    return {
      kind: 'reflection',
      measurementIndex: measurementDefinitions.length,
    }
  }

  return { kind: 'measurement', measurementIndex: nextMeasurementIndex }
}

function perMeasurementProgressSlots(
  measurementDefinition: MeasurementDefinitionLike,
): number {
  const recordingSlots = measurementRequiresRecording(measurementDefinition)
    ? 2
    : 1
  return (
    recordingSlots +
    (measurementHasRecordingInstructions(measurementDefinition) ? 1 : 0)
  )
}

/**
 * Total progress slots: per measurement (recording instructions when present,
 * record/question slots) + 1 trailing slot for reflection.
 * The intro screen itself does not occupy a slot.
 */
export function getProgressTotal(
  measurementDefinitions: readonly MeasurementDefinitionLike[],
): number {
  return (
    measurementDefinitions.reduce(
      (acc, definition) => acc + perMeasurementProgressSlots(definition),
      0,
    ) + 1
  )
}

export function getProgressCurrent(
  state: CurrentScreenStateInput,
  measurementDefinitions: readonly MeasurementDefinitionLike[],
): number {
  const screen = getCurrentScreen(state, measurementDefinitions)
  if (screen.kind === 'intro') {
    return 0
  }
  if (screen.kind === 'reflection') {
    return getProgressTotal(measurementDefinitions)
  }
  const completedBefore = measurementDefinitions
    .slice(0, screen.measurementIndex)
    .reduce(
      (acc, definition) => acc + perMeasurementProgressSlots(definition),
      0,
    )
  if (screen.kind === 'recordingInstructions') {
    return completedBefore + 1
  }
  const currentDefinition = measurementDefinitions[screen.measurementIndex]
  if (screen.kind === 'record') {
    return (
      completedBefore +
      (measurementHasRecordingInstructions(currentDefinition) ? 2 : 1)
    )
  }
  return (
    completedBefore +
    (currentDefinition != null
      ? perMeasurementProgressSlots(currentDefinition)
      : 1)
  )
}

/**
 * How many measurements the user has fully completed (recorded +
 * answered the post-measurement questions for). Used by flow analytics.
 */
export function getCompletedMeasurementsCount(
  state: Pick<VoiceAssessmentState, 'measurementIndex'>,
  measurementDefinitions: readonly MeasurementDefinitionLike[],
): number {
  return Math.min(
    Math.max(state.measurementIndex, 0),
    measurementDefinitions.length,
  )
}

export function buildBackendResumeState({
  measurementDefinitions,
  measurements,
}: {
  measurementDefinitions: readonly ResumeMeasurementDefinitionLike[]
  measurements: readonly SessionResumeMeasurementLike[]
}): BackendResumeState {
  const measurementsByDefinitionId = new Map<
    number,
    SessionResumeMeasurementLike[]
  >()
  for (const measurement of measurements) {
    const existing =
      measurementsByDefinitionId.get(measurement.definition.id) ?? []
    existing.push(measurement)
    measurementsByDefinitionId.set(measurement.definition.id, existing)
  }

  const persistedMeasurementIds = measurementDefinitions.map((definition) => {
    const definitionMeasurements =
      measurementsByDefinitionId.get(definition.id) ?? []
    const persistedMeasurementIndex = definitionMeasurements.findIndex(
      (measurement) => {
        const backendResumeMeasurementComplete =
          isBackendResumeMeasurementComplete(definition, measurement)
        return backendResumeMeasurementComplete
      },
    )

    const persistedMeasurement =
      persistedMeasurementIndex === -1
        ? null
        : definitionMeasurements[persistedMeasurementIndex]

    if (persistedMeasurementIndex !== -1) {
      definitionMeasurements.splice(persistedMeasurementIndex, 1)
    }
    measurementsByDefinitionId.set(definition.id, definitionMeasurements)
    const persistedMeasurementId = persistedMeasurement?.id ?? null
    return persistedMeasurementId
  })

  const firstMissingMeasurementIndex = persistedMeasurementIds.findIndex(
    (measurementId) => measurementId == null,
  )

  return {
    measurementIndex:
      firstMissingMeasurementIndex === -1
        ? measurementDefinitions.length
        : firstMissingMeasurementIndex,
    persistedMeasurementIds,
  }
}

export function resolveBackendResumeTarget({
  measurementDefinitions,
  measurements,
  sessionStatus,
}: {
  measurementDefinitions: readonly ResumeMeasurementDefinitionLike[]
  measurements: readonly SessionResumeMeasurementLike[]
  sessionStatus?: SessionStatus
}): BackendResumeState & { target: BackendResumeTarget } {
  const state = buildBackendResumeState({
    measurementDefinitions,
    measurements,
  })

  return {
    ...state,
    target:
      sessionStatus === 'Completed' || sessionStatus === 'PendingAnalysis'
        ? { kind: 'finish' }
        : state.measurementIndex >= measurementDefinitions.length
          ? { kind: 'reflection' }
          : { kind: 'measurement', measurementIndex: state.measurementIndex },
  }
}
